import './PeriscopeSummary.scss';

import { appActions } from '@app';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import ErrorBoundary from 'antd/es/alert/ErrorBoundary';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { NoData } from '@ui/NoData';
import { PeriscopeBodyComponent } from './types';
import { SonarText } from '@ui/text';
import { Space } from 'antd';

import {
	ApexClassScope,
	ApexTriggerScope,
	EmailAlertScope,
	EmailTemplateScope,
	EntityScope,
	FieldScope,
	FieldUpdateScope,
	FlowScope,
	PageLayoutScope,
	PardotAutomationRuleScope,
	PardotEmailScope,
	PardotEmailTemplateScope,
	PardotFieldScope,
	PardotFormScope,
	PardotLandingPageScope,
	PardotListScope,
	PardotObjectScope,
	PardotPageActionScope,
	PardotProfileScope,
	PicklistValueScope,
	ReportScope,
	TagScope,
	ValidationRuleScope,
	WorkflowRuleScope
} from '@periscopes/scopes';
import { Else, If, Then } from 'react-if';
import {
	PeriscopeSummary as PericopsSummaryType,
	PeriscopeCard,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';
import { PeriscopeHeader, PeriscopeTitle } from './general';
import React, { useEffect } from 'react';
import { useAction, usePeriscopeSummary } from '@hooks';

const periscopeComponentLookup: Record<SonarObjectType, PeriscopeBodyComponent | null> = {
	[ SonarObjectType.PardotAutomation ]: PardotAutomationRuleScope,
	[ SonarObjectType.PardotEmail ]: PardotEmailScope,
	[ SonarObjectType.PardotEmailTemplate ]: PardotEmailTemplateScope,
	[ SonarObjectType.PardotEntity ]: PardotObjectScope,
	[ SonarObjectType.PardotField ]: PardotFieldScope,
	[ SonarObjectType.PardotForm ]: PardotFormScope,
	[ SonarObjectType.PardotLandingPage ]: PardotLandingPageScope,
	[ SonarObjectType.PardotList ]: PardotListScope,
	[ SonarObjectType.PardotPageAction ]: PardotPageActionScope,
	[ SonarObjectType.PardotProfile ]: PardotProfileScope,
	[ SonarObjectType.SalesforceApexClass ]: ApexClassScope,
	[ SonarObjectType.SalesforceApexTrigger ]: ApexTriggerScope,
	[ SonarObjectType.SalesforceEmailTemplate ]: EmailTemplateScope,
	[ SonarObjectType.SalesforceEntity ]: EntityScope,
	[ SonarObjectType.SalesforceField ]: FieldScope,
	[ SonarObjectType.SalesforceFieldUpdate ]: FieldUpdateScope,
	[ SonarObjectType.SalesforceFlow ]: FlowScope,
	[ SonarObjectType.SalesforcePageLayout ]: PageLayoutScope,
	[ SonarObjectType.SalesforcePicklistValue ]: PicklistValueScope,
	[ SonarObjectType.SalesforceProfile ]: null,
	[ SonarObjectType.SalesforceProcessBuilder ]: FlowScope,
	[ SonarObjectType.SalesforceReport ]: ReportScope,
	[ SonarObjectType.SalesforceValidationRule ]: ValidationRuleScope,
	[ SonarObjectType.SalesforceWorkflowAlert ]: EmailAlertScope,
	[ SonarObjectType.SalesforceWorkflowRule ]: WorkflowRuleScope,
	[ SonarObjectType.SonarTag ]: TagScope
};

export interface PeriscopeSummaryProps {
	index: number;
	periscope: PeriscopeCard;
}

export const PeriscopeSummary: React.FC<PeriscopeSummaryProps> = ({ index, periscope }) => {
	const { headerLabel, id: sonarObjectId, name, type, subType } = periscope;

	const SummaryComponent = periscopeComponentLookup[ type ];
	const updatePeriscope = useAction(appActions.updatePeriscopeInfo);

	const { loading, summary } = usePeriscopeSummary(sonarObjectId, type);

	useEffect(() => {
		if (!periscope.name && summary) {
			updatePeriscope({
				name: summary.displayName,
				periscopeIndex: index
			});
		}
	}, [ index, periscope.name, summary, updatePeriscope ]);

	if (!SummaryComponent) {
		return null;
	}

	return (
		<ErrorBoundary>
			<div
				className='periscope-card'
				style={ { zIndex: 100 - index } }>
				<If condition={ sonarObjectId }>
					<Then>
						<PeriscopeHeader
							headerLabel={ headerLabel }
							index={ index }
							sonarObjectType={ type }
							subType={ subType }
							url={ summary?.sourceUrl } />
						<div className='periscope-content'>
							<If condition={ !loading && Boolean(summary) }>
								<Then>
									<PeriscopeTitle data={ summary as PericopsSummaryType } />
									<SummaryComponent
										data={ summary as PericopsSummaryType }
										index={ index } />
								</Then>
								<Else>
									<LoadingSpinner
										size='large'
										spinning />
								</Else>
							</If>
						</div>
					</Then>
					<Else>
						<div className='periscope-no-data'>
							<NoData
								description={
									<Space direction='vertical'>
										<SonarText weight='semibold'>
											{ COPY_SPECIFIC.NO_DATA_FOR_LABEL(headerLabel ?? name) }
										</SonarText>
										<SonarText>
											<div>{ COPY_SPECIFIC.NO_DATA_IMPORTED_LINE_1 }</div>
											<div>{ COPY_SPECIFIC.NO_DATA_IMPORTED_LINE_2 }</div>
										</SonarText>
									</Space>
								} />
						</div>
					</Else>
				</If>
			</div>
		</ErrorBoundary>
	);
};
